import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { analyticsAPI } from '../utils/api';
import { useSiteStore } from '../stores/siteStore';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  ChartBarIcon,
  ArrowDownTrayIcon,
  CalendarDaysIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  VideoCameraIcon,
  MapPinIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline';

export default function AnalyticsPage() {
  const { selectedSite } = useSiteStore();
  const [dateRange, setDateRange] = useState('7d');
  const [cameraSort, setCameraSort] = useState({ field: 'alertCount', dir: 'desc' });
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [zonePage, setZonePage] = useState(0);
  const ZONE_PAGE_SIZE = 6;

  // Single combined query - fetches all analytics data in one request
  // Use dateRange as the key (not startDate) to avoid queryKey instability
  const { data: analyticsData, isLoading, isFetching } = useQuery({
    queryKey: ['analytics-all', selectedSite?.id, dateRange, customStartDate, customEndDate],
    queryFn: () => {
      const params = { siteId: selectedSite?.id, period: dateRange };
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }
      return analyticsAPI.getAll(params);
    },
    enabled: Boolean(selectedSite?.id && (dateRange !== 'custom' || (customStartDate && customEndDate))),
    staleTime: 60000, // Cache for 1 minute
    refetchOnMount: false,
  });

  // Incidents analytics query
  const { data: incidentAnalytics, isLoading: loadingIncidents } = useQuery({
    queryKey: ['analytics-incidents', selectedSite?.id, dateRange, customStartDate, customEndDate],
    queryFn: () => {
      const params = { siteId: selectedSite?.id, period: dateRange };
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }
      return analyticsAPI.getIncidents(params);
    },
    enabled: Boolean(selectedSite?.id && (dateRange !== 'custom' || (customStartDate && customEndDate))),
    staleTime: 60000,
    refetchOnMount: false,
  });

  // Extract data from combined response
  const loadingTrends = isLoading;
  const loadingBreakdown = isLoading;
  const loadingZones = isLoading;
  const loadingCameras = isLoading;
  const loadingResponse = isLoading;
  const loadingHeatmap = isLoading;

  const handleExport = async () => {
    try {
      const response = await analyticsAPI.exportData({
        siteId: selectedSite?.id,
        period: dateRange,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics-${selectedSite?.name}-${dateRange}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Report exported');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  // Extract and transform data from combined response
  const allData = analyticsData?.data || {};


  // Transform compliance trends (add 'violations' alias for violationCount)
  const trends = (allData.complianceTrends || []).map(t => ({
    ...t,
    violations: t.violationCount || 0,
  }));

  // Transform alert breakdown (convert objects to arrays with _count)
  const rawBreakdown = allData.alertBreakdown || {};
  const breakdown = {
    byType: Object.entries(rawBreakdown.byType || {}).map(([type, count]) => ({
      type,
      _count: count,
    })),
    bySeverity: Object.entries(rawBreakdown.bySeverity || {}).map(([severity, count]) => ({
      severity,
      _count: count,
    })),
    total: rawBreakdown.total || 0,
  };

  // Transform zones (add expected field aliases)
  const zones = (allData.zoneStats || []).map(z => ({
    zoneId: z.id,
    zoneName: z.name,
    totalViolations: z.alertCount || 0,
    complianceRate: null, // N/A - will be calculated from PPEComplianceLog in Phase 2
    ...z,
  }));

  // Transform cameras (add expected field aliases)
  const camerasRaw = (allData.cameraPerformance || []).map(c => ({
    cameraId: c.id,
    cameraName: c.name,
    totalDetections: c.detections || 0,
    totalViolations: c.alertCount || 0,
    complianceRate: c.complianceRate || 100,
    alertCount: c.alertCount || 0,
    ...c,
  }));

  // Sort cameras based on cameraSort state
  const cameras = useMemo(() => {
    const sorted = [...camerasRaw];
    sorted.sort((a, b) => {
      const aVal = a[cameraSort.field] ?? 0;
      const bVal = b[cameraSort.field] ?? 0;
      return cameraSort.dir === 'desc' ? bVal - aVal : aVal - bVal;
    });
    return sorted;
  }, [camerasRaw, cameraSort]);

  // Transform response times (convert seconds to minutes)
  const rawResponse = allData.responseTimes || {};
  const response = {
    avgAcknowledgeMinutes: rawResponse.avgAcknowledgement ? rawResponse.avgAcknowledgement / 60 : null,
    avgResolveMinutes: rawResponse.avgResolution ? rawResponse.avgResolution / 60 : null,
    acknowledgeRate: rawResponse.total > 0 ? (rawResponse.acknowledged / rawResponse.total) * 100 : 0,
  };

  // Transform heatmap (convert 2D array to flat array with dayOfWeek, hour, _count)
  // Include ALL hours/days even with 0 count so the grid always renders
  const rawHeatmap = allData.heatmap?.data || [];
  const heatmapData = [];
  const hasHeatmapData = rawHeatmap.length > 0;
  rawHeatmap.forEach((dayData, dayOfWeek) => {
    if (Array.isArray(dayData)) {
      dayData.forEach((count, hour) => {
        heatmapData.push({ dayOfWeek, hour, _count: count || 0 });
      });
    }
  });

  // Calculate summary stats
  const avgCompliance =
    trends.length > 0
      ? (trends.reduce((sum, t) => sum + t.complianceRate, 0) / trends.length).toFixed(1)
      : 0;
  const totalViolations = trends.reduce((sum, t) => sum + t.violations, 0);
  const totalDetections = trends.reduce((sum, t) => sum + t.totalDetections, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold gradient-text">Analytics & Reporting</h1>
          <p className="text-gray-400 mt-1">Safety performance insights and trends</p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {/* Date Range Selector */}
          <div className="glass rounded-xl p-1 inline-flex">
            {[
              { value: '7d', label: '7 Days' },
              { value: '30d', label: '30 Days' },
              { value: '90d', label: '90 Days' },
              { value: 'custom', label: 'Custom' },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  setDateRange(option.value);
                  if (option.value !== 'custom') {
                    setCustomStartDate('');
                    setCustomEndDate('');
                  }
                }}
                className={clsx(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300',
                  dateRange === option.value
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                    : 'text-gray-400 hover:text-white'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>

          {/* Custom Date Pickers */}
          {dateRange === 'custom' && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="input-select text-xs px-2 py-1.5"
              />
              <span className="text-slate-500">to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="input-select text-xs px-2 py-1.5"
              />
            </div>
          )}

          <button onClick={handleExport} className="btn btn-secondary flex items-center gap-2">
            <ArrowDownTrayIcon className="w-5 h-5" />
            <span className="hidden sm:inline">Export</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className={clsx('grid grid-cols-2 md:grid-cols-4 gap-4 transition-opacity duration-300', isFetching && !isLoading && 'opacity-50 pointer-events-none')}>
        <SummaryCard
          title="Avg. Compliance"
          value={`${avgCompliance}%`}
          icon={ShieldCheckIcon}
          color="green"
          trend={getTrend(trends, 'complianceRate')}
          index={0}
        />
        <SummaryCard
          title="Total Violations"
          value={totalViolations.toLocaleString()}
          icon={ExclamationTriangleIcon}
          color="red"
          trend={getTrend(trends, 'violations', true)}
          index={1}
        />
        <SummaryCard
          title="Total Detections"
          value={totalDetections.toLocaleString()}
          icon={VideoCameraIcon}
          color="blue"
          index={2}
        />
        <SummaryCard
          title="Avg. Response"
          value={response.avgAcknowledgeMinutes ? `${Math.round(response.avgAcknowledgeMinutes)}m` : 'N/A'}
          icon={ClockIcon}
          color="purple"
          index={3}
        />
      </div>

      {/* Charts Row 1 */}
      <div className={clsx('grid grid-cols-1 lg:grid-cols-2 gap-6 transition-opacity duration-300', isFetching && !isLoading && 'opacity-50 pointer-events-none')}>
        {/* Compliance Trend Chart */}
        <div className="glass-card p-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <div className="flex items-center gap-2 mb-4">
            <ChartBarIcon className="w-5 h-5 text-purple-500" />
            <h3 className="text-xl font-bold text-gray-800 dark:text-white">Compliance Trend</h3>
          </div>
          {loadingTrends ? (
            <LoadingSpinner />
          ) : trends.length > 0 ? (
            <div className="h-64">
              <ComplianceChart data={trends} />
            </div>
          ) : (
            <EmptyState message="No compliance data available" />
          )}
        </div>

        {/* Alert Breakdown */}
        <div className="glass-card p-6 animate-fade-in" style={{ animationDelay: '0.3s' }}>
          <div className="flex items-center gap-2 mb-4">
            <ExclamationTriangleIcon className="w-5 h-5 text-orange-500" />
            <h3 className="text-xl font-bold text-gray-800 dark:text-white">Alert Breakdown</h3>
          </div>
          {loadingBreakdown ? (
            <LoadingSpinner />
          ) : breakdown.byType?.length > 0 ? (
            <div className="space-y-6">
              {/* Total alerts summary */}
              <div className="glass rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-white">{breakdown.total}</div>
                <div className="text-sm text-gray-400">Total Alerts</div>
              </div>

              {/* Severity Donut Chart */}
              {breakdown.bySeverity && breakdown.bySeverity.length > 0 && (
                <div className="flex items-center justify-center">
                  <div className="relative w-32 h-32">
                    <svg width="100%" height="100%" viewBox="0 0 100 100" className="transform -rotate-90">
                      {(() => {
                        const critical = breakdown.bySeverity.find(s => s.severity === 'CRITICAL')?._count || 0;
                        const high = breakdown.bySeverity.find(s => s.severity === 'HIGH')?._count || 0;
                        const medium = breakdown.bySeverity.find(s => s.severity === 'MEDIUM')?._count || 0;
                        const low = breakdown.bySeverity.find(s => s.severity === 'LOW')?._count || 0;
                        const total = critical + high + medium + low || 1;

                        const criticalPct = (critical / total) * 100;
                        const highPct = (high / total) * 100;
                        const mediumPct = (medium / total) * 100;
                        const lowPct = (low / total) * 100;

                        return (
                          <>
                            <circle cx="50" cy="50" r="35" fill="none" stroke="#dc2626" strokeWidth="8" strokeDasharray={`${criticalPct} ${100 - criticalPct}`} strokeLinecap="round" />
                            <circle cx="50" cy="50" r="35" fill="none" stroke="#ea580c" strokeWidth="8" strokeDasharray={`${highPct} ${100 - highPct}`} strokeDashoffset={-criticalPct} strokeLinecap="round" />
                            <circle cx="50" cy="50" r="35" fill="none" stroke="#eab308" strokeWidth="8" strokeDasharray={`${mediumPct} ${100 - mediumPct}`} strokeDashoffset={-(criticalPct + highPct)} strokeLinecap="round" />
                            <circle cx="50" cy="50" r="35" fill="none" stroke="#3b82f6" strokeWidth="8" strokeDasharray={`${lowPct} ${100 - lowPct}`} strokeDashoffset={-(criticalPct + highPct + mediumPct)} strokeLinecap="round" />
                          </>
                        );
                      })()}
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <p className="text-sm font-bold text-white">{breakdown.total}</p>
                        <p className="text-[10px] text-gray-400">alerts</p>
                      </div>
                    </div>
                  </div>
                  <div className="ml-6 space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-600" />
                      <span className="text-xs text-gray-300">CRITICAL: {breakdown.bySeverity.find(s => s.severity === 'CRITICAL')?._count || 0}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-orange-600" />
                      <span className="text-xs text-gray-300">HIGH: {breakdown.bySeverity.find(s => s.severity === 'HIGH')?._count || 0}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      <span className="text-xs text-gray-300">MEDIUM: {breakdown.bySeverity.find(s => s.severity === 'MEDIUM')?._count || 0}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500" />
                      <span className="text-xs text-gray-300">LOW: {breakdown.bySeverity.find(s => s.severity === 'LOW')?._count || 0}</span>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <p className="text-sm text-gray-400 mb-3">By Type</p>
                <div className="space-y-3">
                  {breakdown.byType?.slice(0, 5).map((item, index) => (
                    <BreakdownBar
                      key={item.type}
                      label={formatAlertType(item.type)}
                      value={item._count}
                      total={breakdown.byType.reduce((s, i) => s + i._count, 0)}
                      index={index}
                    />
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-400 mb-3">By Severity</p>
                <div className="flex gap-2 flex-wrap">
                  {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map((sev) => {
                    const item = breakdown.bySeverity?.find(s => s.severity === sev);
                    return (
                      <div key={sev} className="glass rounded-xl p-3 flex-1 min-w-[70px] text-center">
                        <div className={clsx('text-xl font-bold', getSeverityColor(sev))}>
                          {item?._count || 0}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">{sev}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <ShieldCheckIcon className="w-12 h-12 mb-3 opacity-50 text-green-500" />
              <p className="text-green-400 font-medium">No Alerts</p>
              <p className="text-sm text-gray-500 mt-1">No safety alerts in this period</p>
            </div>
          )}
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Violation Heatmap */}
        <div className="glass-card p-6 animate-fade-in" style={{ animationDelay: '0.4s' }}>
          <div className="flex items-center gap-2 mb-1">
            <ClockIcon className="w-5 h-5 text-red-500" />
            <h3 className="text-xl font-bold text-gray-800 dark:text-white">Violation Heatmap</h3>
          </div>
          <p className="text-sm text-gray-400 mb-4 ml-7">When violations occur (by day and hour)</p>
          {loadingHeatmap ? (
            <LoadingSpinner />
          ) : hasHeatmapData ? (
            <ViolationHeatmap data={heatmapData} />
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-gray-500">
              <CalendarDaysIcon className="w-12 h-12 mb-3 opacity-50" />
              <p>No violation pattern data</p>
              <p className="text-xs text-gray-600 mt-1">Heatmap will populate as alerts are detected</p>
            </div>
          )}
        </div>

        {/* Zone Performance */}
        <div className="glass-card p-6 animate-fade-in" style={{ animationDelay: '0.5s' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <MapPinIcon className="w-5 h-5 text-blue-500" />
              <h3 className="text-xl font-bold text-gray-800 dark:text-white">Zone Performance</h3>
            </div>
            {zones.length > ZONE_PAGE_SIZE && (
              <p className="text-xs text-gray-400">
                Showing {Math.min(zonePage * ZONE_PAGE_SIZE + 1, zones.length)}–{Math.min((zonePage + 1) * ZONE_PAGE_SIZE, zones.length)} of {zones.length}
              </p>
            )}
          </div>
          {loadingZones ? (
            <LoadingSpinner />
          ) : zones.length > 0 ? (
            <div className="space-y-3">
              {zones.slice(zonePage * ZONE_PAGE_SIZE, (zonePage + 1) * ZONE_PAGE_SIZE).map((zone, index) => (
                <div
                  key={zone.zoneId}
                  className="glass rounded-xl p-4 flex items-center justify-between animate-fade-in"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                      <MapPinIcon className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{zone.zoneName}</p>
                      <p className="text-sm text-gray-400">{zone.totalViolations} violations</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p
                      className={clsx(
                        'text-lg font-bold',
                        zone.complianceRate === null ? 'text-gray-400' :
                        zone.complianceRate >= 90
                          ? 'text-green-400'
                          : zone.complianceRate >= 70
                          ? 'text-yellow-400'
                          : 'text-red-400'
                      )}
                    >
                      {zone.complianceRate === null ? 'N/A' : `${zone.complianceRate.toFixed(1)}%`}
                    </p>
                    <p className="text-xs text-gray-500">compliance</p>
                  </div>
                </div>
              ))}
              {zones.length > ZONE_PAGE_SIZE && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
                  <button
                    onClick={() => setZonePage(p => Math.max(0, p - 1))}
                    disabled={zonePage === 0}
                    className="px-3 py-1 text-sm text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    ← Previous
                  </button>
                  <span className="text-xs text-gray-400">
                    Page {zonePage + 1} of {Math.ceil(zones.length / ZONE_PAGE_SIZE)}
                  </span>
                  <button
                    onClick={() => setZonePage(p => p + 1)}
                    disabled={(zonePage + 1) * ZONE_PAGE_SIZE >= zones.length}
                    className="px-3 py-1 text-sm text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          ) : (
            <EmptyState message="No zone data available" />
          )}
        </div>
      </div>

      {/* Camera Performance */}
      <div className="glass-card p-6 animate-fade-in" style={{ animationDelay: '0.6s' }}>
        <div className="flex items-center gap-2 mb-4">
          <VideoCameraIcon className="w-5 h-5 text-green-500" />
          <h3 className="text-xl font-bold text-gray-800 dark:text-white">Camera Performance</h3>
        </div>
        {loadingCameras ? (
          <LoadingSpinner />
        ) : cameras.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="glass-table">
              <thead>
                <tr>
                  <th>Camera</th>
                  <th
                    className="text-left cursor-pointer hover:text-green-400 transition-colors"
                    onClick={() => {
                      if (cameraSort.field === 'totalDetections') {
                        setCameraSort(s => ({ field: s.field, dir: s.dir === 'desc' ? 'asc' : 'desc' }));
                      } else {
                        setCameraSort({ field: 'totalDetections', dir: 'desc' });
                      }
                    }}
                  >
                    Detections {cameraSort.field === 'totalDetections' && (cameraSort.dir === 'desc' ? '↓' : '↑')}
                  </th>
                  <th
                    className="text-left cursor-pointer hover:text-green-400 transition-colors"
                    onClick={() => {
                      if (cameraSort.field === 'totalViolations') {
                        setCameraSort(s => ({ field: s.field, dir: s.dir === 'desc' ? 'asc' : 'desc' }));
                      } else {
                        setCameraSort({ field: 'totalViolations', dir: 'desc' });
                      }
                    }}
                  >
                    Violations {cameraSort.field === 'totalViolations' && (cameraSort.dir === 'desc' ? '↓' : '↑')}
                  </th>
                  <th
                    className="text-left cursor-pointer hover:text-green-400 transition-colors"
                    onClick={() => {
                      if (cameraSort.field === 'complianceRate') {
                        setCameraSort(s => ({ field: s.field, dir: s.dir === 'desc' ? 'asc' : 'desc' }));
                      } else {
                        setCameraSort({ field: 'complianceRate', dir: 'desc' });
                      }
                    }}
                  >
                    Compliance {cameraSort.field === 'complianceRate' && (cameraSort.dir === 'desc' ? '↓' : '↑')}
                  </th>
                  <th
                    className="text-left cursor-pointer hover:text-green-400 transition-colors"
                    onClick={() => {
                      if (cameraSort.field === 'alertCount') {
                        setCameraSort(s => ({ field: s.field, dir: s.dir === 'desc' ? 'asc' : 'desc' }));
                      } else {
                        setCameraSort({ field: 'alertCount', dir: 'desc' });
                      }
                    }}
                  >
                    Alerts {cameraSort.field === 'alertCount' && (cameraSort.dir === 'desc' ? '↓' : '↑')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {cameras.map((cam, index) => (
                  <tr key={cam.cameraId} className="animate-fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                          <VideoCameraIcon className="w-4 h-4 text-purple-400" />
                        </div>
                        <span className="font-medium text-gray-900 dark:text-white">{cam.cameraName}</span>
                      </div>
                    </td>
                    <td className="text-left text-gray-300">
                      {cam.totalDetections.toLocaleString()}
                    </td>
                    <td className="text-left text-gray-300">
                      {cam.totalViolations.toLocaleString()}
                    </td>
                    <td className="text-left">
                      <span
                        className={clsx(
                          'font-semibold',
                          cam.complianceRate >= 90
                            ? 'text-green-400'
                            : cam.complianceRate >= 70
                            ? 'text-yellow-400'
                            : 'text-red-400'
                        )}
                      >
                        {(cam.complianceRate ?? 0).toFixed(1)}%
                      </span>
                    </td>
                    <td className="text-left text-gray-300">{cam.alertCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState message="No camera data available" />
        )}
      </div>

      {/* Incidents Analytics */}
      <div className="glass-card p-6 animate-fade-in" style={{ animationDelay: '0.65s' }}>
        <div className="flex items-center gap-2 mb-4">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
          <h3 className="text-xl font-bold text-gray-800 dark:text-white">Incident Analytics</h3>
        </div>

        {loadingIncidents ? (
          <LoadingSpinner />
        ) : incidentAnalytics?.data ? (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="glass rounded-xl p-4">
                <p className="text-sm text-gray-400 mb-2">Total Incidents</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{incidentAnalytics.data.summary?.total || 0}</p>
              </div>
              <div className="glass rounded-xl p-4">
                <p className="text-sm text-gray-400 mb-2">Near Misses</p>
                <p className="text-3xl font-bold text-blue-400">{incidentAnalytics.data.summary?.nearMissCount || 0}</p>
              </div>
              <div className="glass rounded-xl p-4">
                <p className="text-sm text-gray-400 mb-2">OSHA Reportable</p>
                <p className="text-3xl font-bold text-orange-400">{incidentAnalytics.data.summary?.oshaReportableCount || 0}</p>
              </div>
              <div className="glass rounded-xl p-4">
                <p className="text-sm text-gray-400 mb-2">Days Without Incident</p>
                <p className="text-3xl font-bold text-green-400">{incidentAnalytics.data.summary?.daysWithoutIncident || 0}</p>
              </div>
            </div>

            {/* Incident Type Breakdown */}
            {Object.keys(incidentAnalytics.data.byType || {}).length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-400 mb-3">By Incident Type</h4>
                <div className="space-y-2">
                  {Object.entries(incidentAnalytics.data.byType || {}).map(([type, count]) => {
                    const maxCount = Math.max(...Object.values(incidentAnalytics.data.byType || {}), 1);
                    const pct = (count / maxCount) * 100;
                    const colors = {
                      near_miss: 'bg-blue-500',
                      first_aid: 'bg-yellow-500',
                      medical_treatment: 'bg-orange-500',
                      restricted_work: 'bg-red-500',
                      lost_time: 'bg-red-600',
                      property_damage: 'bg-purple-500',
                      environmental: 'bg-green-500',
                    };
                    const color = colors[type] || 'bg-gray-500';
                    return (
                      <div key={type} className="flex items-center gap-3">
                        <span className="text-xs font-medium text-gray-400 w-32 capitalize">{type.replace(/_/g, ' ')}</span>
                        <div className="flex-1 h-6 bg-gray-700/30 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs font-bold text-gray-300 w-8 text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Incident Status Overview */}
            {Object.keys(incidentAnalytics.data.byStatus || {}).length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-400 mb-3">By Status</h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(incidentAnalytics.data.byStatus || {}).map(([status, count]) => {
                    const statusColors = {
                      open: 'bg-blue-500/20 text-blue-400 border-blue-400',
                      investigating: 'bg-yellow-500/20 text-yellow-400 border-yellow-400',
                      pending_action: 'bg-orange-500/20 text-orange-400 border-orange-400',
                      resolved: 'bg-green-500/20 text-green-400 border-green-400',
                      closed: 'bg-gray-500/20 text-gray-400 border-gray-400',
                    };
                    const styling = statusColors[status] || statusColors.closed;
                    return (
                      <div key={status} className={`px-3 py-1.5 rounded-full border text-xs font-medium ${styling} capitalize`}>
                        {status.replace(/_/g, ' ')}: <span className="font-bold">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Weekly Trend */}
            {(incidentAnalytics.data.weeklyTrend || []).length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-400 mb-3">Weekly Trend</h4>
                <div className="flex items-end gap-2 h-32 bg-gray-700/10 rounded-lg p-4">
                  {incidentAnalytics.data.weeklyTrend.map((week) => {
                    const maxCount = Math.max(...(incidentAnalytics.data.weeklyTrend || []).map(w => w.count), 1);
                    const height = (week.count / maxCount) * 100;
                    const weekDate = new Date(week.week);
                    const weekLabel = `${weekDate.getMonth() + 1}/${weekDate.getDate()}`;
                    return (
                      <div key={week.week} className="flex-1 flex flex-col items-center gap-2">
                        <div className="w-full h-full flex items-end justify-center">
                          <div className="w-2/3 bg-red-500 rounded-t" style={{ height: `${height}%`, minHeight: '4px' }} />
                        </div>
                        <span className="text-xs text-gray-500">{weekLabel}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          <EmptyState message="No incident data available" />
        )}
      </div>

      {/* Response Times */}
      <div className="glass-card p-6 animate-fade-in" style={{ animationDelay: '0.7s' }}>
        <div className="flex items-center gap-2 mb-4">
          <ClockIcon className="w-5 h-5 text-purple-500" />
          <h3 className="text-xl font-bold text-gray-800 dark:text-white">Response Time Analysis</h3>
        </div>
        {loadingResponse ? (
          <LoadingSpinner />
        ) : response.avgAcknowledgeMinutes ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="glass rounded-xl p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mx-auto mb-3">
                <ClockIcon className="w-6 h-6 text-blue-400" />
              </div>
              <p className="text-3xl font-bold text-blue-400">
                {Math.round(response.avgAcknowledgeMinutes)}m
              </p>
              <p className="text-sm text-gray-400 mt-1">Avg. Acknowledgement</p>
            </div>
            <div className="glass rounded-xl p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center mx-auto mb-3">
                <ShieldCheckIcon className="w-6 h-6 text-green-400" />
              </div>
              <p className="text-3xl font-bold text-green-400">
                {Math.round(response.avgResolveMinutes || 0)}m
              </p>
              <p className="text-sm text-gray-400 mt-1">Avg. Resolution</p>
            </div>
            <div className="glass rounded-xl p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mx-auto mb-3">
                <ChartBarIcon className="w-6 h-6 text-purple-400" />
              </div>
              <p className="text-3xl font-bold text-purple-400">
                {response.acknowledgeRate?.toFixed(1) || 0}%
              </p>
              <p className="text-sm text-gray-400 mt-1">Acknowledgement Rate</p>
            </div>
          </div>
        ) : (
          <EmptyState message="No acknowledged alerts in this period" />
        )}
      </div>
    </div>
  );
}

// Helper Components
function SummaryCard({ title, value, icon: Icon, color, trend, index }) {
  const colorConfig = {
    green: { bg: 'bg-green-500/20', border: 'border-green-500/30', text: 'text-green-400', glow: 'glow-green' },
    red: { bg: 'bg-red-500/20', border: 'border-red-500/30', text: 'text-red-400', glow: 'glow-red' },
    blue: { bg: 'bg-blue-500/20', border: 'border-blue-500/30', text: 'text-blue-400', glow: 'glow-blue' },
    purple: { bg: 'bg-purple-500/20', border: 'border-purple-500/30', text: 'text-purple-400', glow: 'glow-purple' },
  };

  const config = colorConfig[color];

  return (
    <div
      className="glass-card p-5 transition-all duration-300 hover:scale-[1.02] animate-slide-up"
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={clsx('w-12 h-12 rounded-xl flex items-center justify-center', config.bg, config.border, 'border')}>
          <Icon className={clsx('w-6 h-6', config.text)} />
        </div>
        {trend && (
          <div
            className={clsx(
              'flex items-center gap-1 text-sm px-2 py-1 rounded-lg',
              trend.isPositive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
            )}
          >
            {trend.isPositive ? (
              <ArrowTrendingUpIcon className="w-4 h-4" />
            ) : (
              <ArrowTrendingDownIcon className="w-4 h-4" />
            )}
            {Math.abs(trend.value).toFixed(1)}%
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      <p className="text-sm text-gray-400 mt-1">{title}</p>
    </div>
  );
}

function ComplianceChart({ data }) {
  // Check if we have meaningful detection data
  const totalDetections = data.reduce((sum, d) => sum + (d.totalDetections || 0), 0);
  const hasDetectionData = totalDetections > 0;

  // If no detection data, show informative message
  if (!hasDetectionData) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-6 text-center max-w-sm">
          <ExclamationTriangleIcon className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">No PPE Detection Data</p>
          <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
            The AI engine hasn't logged any PPE compliance data for this period.
            Ensure cameras are connected and the detection engine is running.
          </p>
        </div>
      </div>
    );
  }

  const maxCompliance = Math.max(...data.map(d => d.complianceRate), 100);

  return (
    <div className="flex h-full">
      {/* Y-axis labels */}
      <div className="flex flex-col justify-between text-xs text-gray-600 dark:text-gray-400 pr-2 py-1 font-medium">
        <span>100%</span>
        <span>75%</span>
        <span>50%</span>
        <span>25%</span>
        <span>0%</span>
      </div>

      {/* Chart area */}
      <div className="flex-1 flex flex-col">
        {/* Bars container with grid lines */}
        <div className="flex-1 relative border-l border-b border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-transparent rounded-tr-lg">
          {/* Horizontal grid lines */}
          <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
            {[0, 1, 2, 3, 4].map(i => (
              <div key={i} className="border-t border-gray-200 dark:border-white/10 w-full" />
            ))}
          </div>

          {/* Bars */}
          <div className="absolute inset-0 flex items-end justify-around px-2 pb-0">
            {data.map((item, index) => {
              const height = item.complianceRate;
              // Color based on compliance level
              const barBg = height >= 90
                ? 'bg-gradient-to-t from-green-600 to-green-400'
                : height >= 70
                ? 'bg-gradient-to-t from-yellow-500 to-yellow-400'
                : 'bg-gradient-to-t from-red-600 to-red-400';

              return (
                <div key={index} className="flex-1 flex flex-col items-center group mx-1 h-full">
                  <div className="relative w-full h-full flex items-end justify-center">
                    {/* Bar */}
                    <div
                      className={`w-full max-w-[45px] ${barBg} rounded-t-md shadow-sm transition-all duration-300 hover:opacity-90 cursor-pointer`}
                      style={{
                        height: `${Math.max(height, 5)}%`,
                        minHeight: height > 0 ? '8px' : '4px'
                      }}
                    />
                    {/* Value label on top of bar */}
                    <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-xs font-bold text-gray-700 dark:text-gray-300">
                      {Math.round(item.complianceRate)}%
                    </div>
                    {/* Tooltip on hover */}
                    <div className="absolute -top-20 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-800 px-3 py-2 rounded-lg text-xs text-white whitespace-nowrap z-20 shadow-xl pointer-events-none">
                      <div className="font-bold text-sm">{(item.complianceRate ?? 0).toFixed(1)}% Compliance</div>
                      <div className="text-gray-300 mt-1">{item.totalDetections || 0} people detected</div>
                      <div className="text-red-300">{item.violations || 0} violations</div>
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-gray-800"></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* X-axis labels */}
        <div className="flex justify-around pt-3 border-l border-transparent">
          {data.map((item, index) => (
            <div key={index} className="flex-1 text-center">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400 truncate px-1">
                {formatDate(item.date)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BreakdownBar({ label, value, total, index }) {
  const percentage = total > 0 ? (value / total) * 100 : 0;

  return (
    <div className="animate-fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-300">{label}</span>
        <span className="text-gray-400">{value}</span>
      </div>
      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-500"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

function ViolationHeatmap({ data }) {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const hours = Array.from({ length: 24 }, (_, i) => i);

  // Create grid data
  const grid = {};
  data.forEach((item) => {
    const key = `${item.dayOfWeek}-${item.hour}`;
    grid[key] = item._count;
  });

  const maxCount = Math.max(...data.map((d) => d._count), 1);
  const totalViolations = data.reduce((sum, d) => sum + d._count, 0);

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        {/* Hour labels */}
        <div className="flex mb-1">
          <div className="w-10"></div>
          {hours.filter((_, i) => i % 3 === 0).map((hour) => (
            <div key={hour} className="w-[36px] text-center text-xs text-gray-500">
              {hour}:00
            </div>
          ))}
        </div>
        {/* Day rows */}
        {days.map((day, dayIndex) => (
          <div key={day} className="flex items-center mb-1">
            <div className="w-10 text-xs text-gray-500 font-medium">{day}</div>
            {hours.map((hour) => {
              const count = grid[`${dayIndex}-${hour}`] || 0;
              const intensity = count / maxCount;
              return (
                <div
                  key={hour}
                  className="w-3 h-4 mx-0.5 rounded-sm transition-all duration-200 hover:scale-125 cursor-pointer"
                  style={{
                    backgroundColor: count > 0
                      ? `rgba(239, 68, 68, ${intensity * 0.8 + 0.2})`
                      : 'rgba(255, 255, 255, 0.05)',
                  }}
                  title={`${day} ${hour}:00 - ${count} violations`}
                />
              );
            })}
          </div>
        ))}
      </div>

      {/* Legend and summary */}
      <div className="flex items-center justify-between pt-2 border-t border-white/10">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Less</span>
          <div className="flex gap-0.5">
            {[0.1, 0.3, 0.5, 0.7, 0.9].map((intensity, i) => (
              <div
                key={i}
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: `rgba(239, 68, 68, ${intensity})` }}
              />
            ))}
          </div>
          <span className="text-xs text-gray-500">More</span>
        </div>
        <div className="text-xs text-gray-400">
          Total: <span className="text-white font-medium">{totalViolations}</span> violations
        </div>
      </div>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-48">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500/30 border-t-purple-500"></div>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center h-48 text-gray-500">
      <ChartBarIcon className="w-12 h-12 mb-3 opacity-50" />
      <p>{message}</p>
    </div>
  );
}

// Helper functions
function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatAlertType(type) {
  return type?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) || '';
}

function getSeverityColor(severity) {
  const colors = {
    CRITICAL: 'text-red-400',
    HIGH: 'text-orange-400',
    MEDIUM: 'text-yellow-400',
    LOW: 'text-blue-400',
  };
  return colors[severity] || 'text-gray-400';
}

function getTrend(data, field, inverse = false) {
  if (!data || data.length < 2) return null;

  const recent = data.slice(-3);
  const earlier = data.slice(0, 3);

  const recentAvg = recent.reduce((s, d) => s + d[field], 0) / recent.length;
  const earlierAvg = earlier.reduce((s, d) => s + d[field], 0) / earlier.length;

  if (earlierAvg === 0) return null;

  const change = ((recentAvg - earlierAvg) / earlierAvg) * 100;

  return {
    value: change,
    isPositive: inverse ? change < 0 : change > 0,
  };
}
